import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Fingerprint, Loader2, ShieldCheck, Trash2 } from "lucide-react";
import { fetchBiometricStatus, disableBiometric, uploadBiometricSelfie } from "@/lib/api";
import { enrollBiometric, isBiometricAvailable, isMobile, detectPlatform, PASSKEY_FLAG } from "@/lib/webauthn";
import { SelfieCapture } from "@/components/SelfieCapture";
import { BiometricGuide } from "@/components/BiometricGuide";

export const BiometricSection = () => {
  const [available, setAvailable] = useState(false);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selfieOpen, setSelfieOpen] = useState(false);

  const load = () => fetchBiometricStatus().then(setStatus).catch(() => setStatus({ enrolled: false, credentials: [] }));

  useEffect(() => {
    isBiometricAvailable().then(setAvailable);
    load();
  }, []);

  const enable = () => setSelfieOpen(true);

  const onSelfieDone = async (photo) => {
    setSelfieOpen(false);
    setBusy(true);
    try {
      await enrollBiometric();
      if (photo) {
        try { await uploadBiometricSelfie({ image: photo, kind: "reference", context: "enrollment" }); } catch { /* non-blocking */ }
      }
      toast.success("Empreinte biometrique activee ! Vos paiements sont desormais proteges.");
      load();
    } catch (e) {
      toast.error(e.message || "Echec de l'activation biometrique");
    }
    setBusy(false);
  };

  const disable = async () => {
    if (!window.confirm("Desactiver la protection biometrique ?")) return;
    setBusy(true);
    try {
      await disableBiometric();
      localStorage.removeItem(PASSKEY_FLAG);
      toast.success("Biometrie desactivee");
      load();
    } catch { toast.error("Erreur"); }
    setBusy(false);
  };

  if (!status) return null;

  return (
    <section className="glass-card p-7 max-w-2xl" data-testid="biometric-section">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-400 text-white grid place-items-center shrink-0">
          <Fingerprint size={20} />
        </div>
        <div>
          <div className="font-bold text-lg">Securite biometrique</div>
          <div className="text-xs text-[var(--ink-soft)]">
            Empreinte digitale / Face ID exigee avant chaque paiement, en plus du code SMS. Permet aussi la connexion sans mot de passe.
          </div>
        </div>
      </div>

      {status.enrolled ? (
        <div className="mt-4 space-y-3">
          <div className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 bg-emerald-50 px-3 py-1.5 rounded-full" data-testid="biometric-enabled-badge">
            <ShieldCheck size={14} /> Protection biometrique active
          </div>
          <div className="space-y-1">
            {status.credentials.map((c) => (
              <div key={c.credential_id} className="text-xs text-[var(--ink-soft)]">
                Appareil {c.device_label || "mobile"} — enregistre le {(c.created_at || "").slice(0, 10)}
                {c.last_used_at && ` — derniere utilisation ${(c.last_used_at || "").slice(0, 10)}`}
              </div>
            ))}
          </div>
          <button onClick={disable} disabled={busy} className="btn-secondary !text-red-600 !border-red-200 inline-flex items-center gap-2" data-testid="biometric-disable-btn">
            <Trash2 size={14} /> Desactiver la biometrie
          </button>
        </div>
      ) : available && isMobile() ? (
        <div className="mt-4">
          <BiometricGuide />
          <button onClick={enable} disabled={busy} className="btn-primary inline-flex items-center gap-2 mt-4" data-testid="biometric-enable-btn">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Fingerprint size={16} />}
            Activer l&apos;empreinte digitale ({detectPlatform()})
          </button>
          <p className="text-xs text-[var(--ink-soft)] mt-2">
            Un selfie de reference est pris a l&apos;activation, puis votre empreinte ne quitte jamais votre telephone : seule une cle cryptographique est enregistree (standard WebAuthn).
          </p>
        </div>
      ) : (
        <div className="mt-4">
          <p className="text-sm text-[var(--ink-soft)]" data-testid="biometric-unavailable">
            {isMobile()
              ? "Biometrie non disponible sur cet appareil (capteur non detecte). Suivez le guide ci-dessous puis rechargez la page."
              : "Disponible uniquement sur mobile (Android / iPhone). Ouvrez cette page depuis votre telephone puis suivez le guide en 4 etapes :"}
          </p>
          <div className="mt-3"><BiometricGuide /></div>
        </div>
      )}

      <SelfieCapture
        open={selfieOpen}
        onDone={onSelfieDone}
        title="Selfie de reference"
        description="Cette photo servira de reference anti-fraude pour vos futurs paiements."
      />
    </section>
  );
};
